import React, { useCallback, useEffect, useRef } from 'react'
import Button from 'shared/ui/Button/Button'
import PriorityDrawerShell from 'pages/Dashboard/components/TodaysPrioritySection/components/PriorityDrawerShell/PriorityDrawerShell'
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as StarsIcon } from 'assets/icons/stars.svg'
import LorRichText from '../LorRichText/LorRichText'
import s from './styles/ResultDrawer.module.scss'

const formatGeneratedAt = value => {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	})
}

const ResultDrawer = ({
	application,
	letter,
	drawerMode = 'view',
	savedPillVisible = false,
	onClose,
	onEdit,
	onImprove,
	onCopy,
	onDownload,
	onSaveSnapshot,
	registerEditorFlush,
	setDrawerMode,
}) => {
	const editorRef = useRef(null)
	const lastSavedRef = useRef('')
	const isEditing = drawerMode === 'edit'

	const studentName = application?.studentName || ''
	const studentId = application?.studentId || ''
	const university =
		application?.university || application?.universityName || ''
	const program = application?.program || application?.major || ''
	const item = {
		studentId,
		studentName,
		universityName: university,
		programName: program,
	}
	const meta = [university, program].filter(Boolean).join(' · ')
	const generatedLabel = formatGeneratedAt(
		letter?.updatedAt || letter?.generatedAt,
	)

	const flushSave = useCallback(async () => {
		if (!editorRef.current || !onSaveSnapshot) return null
		const snapshot = editorRef.current.getSnapshot()
		const signature = JSON.stringify(snapshot)
		if (signature === lastSavedRef.current) return letter
		const saved = await onSaveSnapshot(snapshot)
		lastSavedRef.current = signature
		return saved
	}, [letter, onSaveSnapshot])

	useEffect(() => {
		registerEditorFlush?.(flushSave)
		return () => registerEditorFlush?.(null)
	}, [flushSave, registerEditorFlush])

	useEffect(() => {
		if (!isEditing) return undefined
		const timer = window.setInterval(() => {
			flushSave().catch(() => undefined)
		}, 2000)
		return () => window.clearInterval(timer)
	}, [flushSave, isEditing])

	const headerActions = (
		<>
			{isEditing ? (
				<Button
					size="sm"
					variant="secondary"
					onClick={async () => {
						await flushSave()
						setDrawerMode?.('view')
					}}
				>
					Done
				</Button>
			) : (
				<Button
					size="sm"
					variant="secondary"
					onClick={onEdit}
					leftIcon={<PenIcon />}
				>
					Edit
				</Button>
			)}
			<Button
				size="sm"
				variant="secondary"
				onClick={onImprove}
				leftIcon={<StarsIcon />}
			>
				Improve with AI
			</Button>
		</>
	)

	const footerContent = (
		<>
			<span className={s.date}>
				{generatedLabel ? `Generated ${generatedLabel}` : ''}
			</span>
			<div className={s.actions}>
				<Button
					size="sm"
					variant="secondary"
					onClick={onCopy}
					leftIcon={
						<span className={s.material} aria-hidden>
							content_copy
						</span>
					}
				>
					Copy
				</Button>
				<Button
					size="sm"
					variant="secondary"
					onClick={onDownload}
					leftIcon={
						<span className={s.material} aria-hidden>
							download
						</span>
					}
				>
					Download PDF
				</Button>
			</div>
		</>
	)

	return (
		<PriorityDrawerShell
			item={item}
			onClose={onClose}
			meta={meta}
			headerActions={headerActions}
			showFooter={false}
			footerContent={footerContent}
			bodyClassName={s.body}
		>
			<main className={s.pane}>
				<article className={s.letter}>
					{letter?.title ? <h2 className={s.title}>{letter.title}</h2> : null}
					<LorRichText
						ref={editorRef}
						letterId={letter?.id}
						contentJson={letter?.contentJson}
						contentText={letter?.contentHtml || letter?.contentText}
						isEditing={isEditing}
					/>
				</article>
			</main>
			{savedPillVisible ? <span className={s.saved}>Saved</span> : null}
		</PriorityDrawerShell>
	)
}

export default ResultDrawer
